import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ImpersonationService } from './impersonation.service';

/** When the last automatic re-sign-in started, so a token the API keeps refusing can't loop the tab through the broker. */
export const REAUTH_STARTED_AT_KEY = 'idrportalapp.reauthStartedAt';

/** A second 401 within this window of a re-sign-in is left to the page's own error state. */
export const REAUTH_COOLDOWN_MS = 2 * 60 * 1000;

/**
 * Sends the browser back through the broker when this app's API stops accepting its token.
 *
 * The portal keeps showing the signed-in user from its stored session, but the access token behind
 * it can expire — a tab left open for days, or a silent renew that never ran. The API then answers
 * 401, and without this every page showed its "could not load" state to someone who only needed to
 * sign in again (seen on Staging, 2026-09-28).
 *
 * It ends any impersonation first — the exchanged token in sessionStorage is the likelier one to have
 * expired, and it must not survive into the next session — then drops the local session and starts a
 * fresh authorization. A still-valid broker sign-in makes that a quick round trip back.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly impersonationService = inject(ImpersonationService);
  private started = false;

  /** Starts one re-sign-in. False when one is already under way, or one ran too recently to try again. */
  handleUnauthorized(now: number = Date.now()): boolean {
    if (this.started || this.startedRecently(now)) {
      return false;
    }

    this.started = true;
    this.remember(now);
    void this.reauthenticate();
    return true;
  }

  private async reauthenticate(): Promise<void> {
    await this.impersonationService.stopForLogout();
    this.oidcSecurityService.logoffLocal();
    this.oidcSecurityService.authorize();
  }

  private startedRecently(now: number): boolean {
    try {
      const startedAt = Number(sessionStorage.getItem(REAUTH_STARTED_AT_KEY));
      return Number.isFinite(startedAt) && startedAt > 0 && now - startedAt < REAUTH_COOLDOWN_MS;
    } catch {
      return false;
    }
  }

  private remember(now: number): void {
    try {
      sessionStorage.setItem(REAUTH_STARTED_AT_KEY, String(now));
    } catch {
      // Storage blocked: the in-memory flag still stops a loop within this page.
    }
  }
}

/**
 * A 401 from this app's own API means the session behind the page is gone: hand it to
 * SessionExpiryService, and still pass the error on so the caller's own error state shows until the
 * redirect lands. Other hosts — the broker included — and other statuses pass through untouched.
 */
export const sessionExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionExpiry = inject(SessionExpiryService);
  const isApiRequest = environment.apiUrl.length > 0 && req.url.startsWith(environment.apiUrl);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (isApiRequest && error instanceof HttpErrorResponse && error.status === 401) {
        sessionExpiry.handleUnauthorized();
      }

      return throwError(() => error);
    }),
  );
};
